/**
 * Der eine Supabase-Client der App.
 *
 * WARUM EIN MODUL-SINGLETON: `detectSessionInUrl` laeuft beim Anlegen des
 * Clients. Zwei Clients heisst zweimal derselbe Einmal-Code aus einem
 * Bestaetigungslink, und der zweite Versuch scheitert — der Nutzer sieht dann
 * „Link ungueltig" fuer einen Link, der gerade funktioniert hat.
 */

import { createClient } from '@supabase/supabase-js';
import { SUPABASE_PUBLISHABLE_KEY, SUPABASE_URL } from './config';
import { t } from './sprache';

export const supabase = createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    // Sitzung ueber Neuladen hinweg halten und stillschweigend erneuern.
    persistSession: true,
    autoRefreshToken: true,
    // Bestaetigungs- und Wiederherstellungslinks tragen ihren Nachweis in der
    // Adresse. Das hier holt ihn heraus und raeumt die Adresse auf, bevor
    // irgendeine Seite sie zu Gesicht bekommt.
    detectSessionInUrl: true,
    // PKCE statt implizitem Fluss: der Nachweis steht dann als `?code=` in der
    // Adresse und nicht als Token im Fragment. Ein Fragment landet in keinem
    // Server-Log, ein Code ist dafuer einmalig und an diesen Browser gebunden.
    flowType: 'pkce',
  },
});

/**
 * Meldungen des Auth-Servers sind englisch und technisch. Was der Nutzer
 * liest, soll sagen, was er tun kann.
 *
 * Eine Meldung, die wir noch nicht kennen, steht seit 19.10.2026 nicht mehr im
 * Original da (Pruefung der Bedienung vom selben Tag, Befund 2): ein
 * englischer Satz wie „AuthApiError: …" auf einer deutschen Seite sagt dem
 * Nutzer nicht, was er tun kann. Er bekommt einen Ersatzsatz mit dem Weg zu
 * uns, das Original geht in die Konsole und bleibt so meldbar.
 */
export function fehlertext(fehler: { message?: string } | null | undefined): string {
  const m = (fehler?.message ?? '').toLowerCase();
  if (!m) return t('Das hat nicht geklappt. Bitte noch einmal versuchen.', 'That did not work. Please try again.');
  // Netz weg: supabase-js meldet dann die Meldung des Browsers (Chrome
  // „Failed to fetch", Safari „Load failed", Firefox „NetworkError …").
  if (m.includes('failed to fetch') || m.includes('load failed') || m.includes('networkerror')) {
    return t('Keine Verbindung zu 99takes. Prüf deine Internetverbindung.', 'No connection to 99takes. Check your internet connection.');
  }
  if (m.includes('invalid login credentials')) return t('E-Mail oder Passwort stimmt nicht.', 'Email or password is wrong.');
  if (m.includes('email not confirmed')) return t('Diese Adresse ist noch nicht bestätigt. Sieh im Postfach nach — auch im Spam.', 'This address is not confirmed yet. Check your inbox, and your spam folder too.');
  if (m.includes('user already registered')) return t('Für diese Adresse gibt es schon ein Konto. Melde dich an oder setze dein Passwort zurück.', 'There is already an account for this address. Sign in or reset your password.');
  if (m.includes('password should be at least')) return t('Das Passwort ist zu kurz.', 'The password is too short.');
  if (m.includes('weak password')) return t('Das Passwort ist zu einfach. Nimm eines, das länger ist oder mehr Zeichenarten mischt.', 'The password is too simple. Choose one that is longer or mixes more kinds of characters.');
  if (m.includes('rate limit') || m.includes('too many requests')) return t('Zu viele Versuche. Warte ein paar Minuten.', 'Too many attempts. Wait a few minutes.');
  // Turnstile (turnstile.tsx): Token fehlt, ist verbraucht oder abgelaufen.
  // Das Widget ist danach schon zurückgesetzt, ein zweiter Versuch hat einen neuen.
  if (m.includes('captcha')) return t('Die Sicherheitsprüfung ist nicht durchgegangen. Bitte noch einmal versuchen.', 'The security check did not go through. Please try again.');
  if (m.includes('unable to validate email')) return t('Diese E-Mail-Adresse sieht nicht gültig aus.', 'This email address does not look valid.');
  if (m.includes('expired') || m.includes('invalid flow state')) {
    return t('Der Link ist abgelaufen oder wurde schon benutzt. Fordere einen neuen an.', 'The link has expired or was already used. Request a new one.');
  }
  if (m.includes('different from the old password')) return t('Das neue Passwort muss sich vom alten unterscheiden.', 'The new password must be different from the old one.');
  console.warn('[auth] unbekannte Meldung:', fehler!.message);
  return t(
    'Das hat nicht geklappt. Versuch es noch einmal; hilft das nicht, schreib uns an run@99takes.com.',
    'That did not work. Try again; if that does not help, write to us at run@99takes.com.',
  );
}
